import {GetStaticProps, InferGetStaticPropsType} from 'next';
import Head from 'next/head';
import Image from 'next/image';
import React from 'react';

import Col from '../../../../features/common/Col';
import {isNullish, TWENTY_FOUR_HOURS_IN_SECONDS} from '../../../../features/common/util';
import Button from '../../../../features/tool/components/buttons/Button';
import {getToolLayoutWithSupplier} from '../../../../features/tool/components/marketplace/LayoutWithSupplier';
import ReviewList from '../../../../features/tool/components/ReviewsList';
import {ExtendedSupplier} from '../../../../features/tool/marketplace/types';
import {getExtendedSupplier} from '../../../../features/tool/marketplace/util.server';
import {buildStaticPropsResponse} from '../../../../features/tool/util/dataFetch';
import {NextPageWithLayout} from '../../../_app';

type Props = {
  supplier: ExtendedSupplier;
};

const SupplierReviewsPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({supplier}) => {
  return (
    <div className={'mt-sm pb-lg overflow-y-auto [&>*]:shrink-0'}>
      <Head>
        <title key={'title'}>Reviews - {supplier.name} - Envirocrops</title>
      </Head>

      {supplier.reviews && !isNullish(supplier.rating) ? (
        <Col>
          <ReviewList
            title={'Reviews'}
            count={supplier.rating.count}
            average={supplier.rating.average}
            list={supplier.reviews}
          />{' '}
          <div className={'mt-md text-xs text-gray-200'}>Showing most relevant reviews by</div>
          <div>
            <Image src="/img/google_on_white.png" alt="Google logo" width={59} height={18} />
          </div>
          {supplier.url && (
            <Button
              className="mt-md"
              variant="secondary"
              href={supplier.url}
              target="_blank"
              rel="noopener noreferrer"
              external
            >
              Leave a review on Google
            </Button>
          )}
        </Col>
      ) : (
        <p className={'text-sm text-gray-200'}>This supplier does not allow reviews.</p>
      )}
    </div>
  );
};

export const getStaticPaths = async () => ({paths: [], fallback: 'blocking'});

export const getStaticProps: GetStaticProps<Props> = async ({params}) => {
  if (typeof params?.id !== 'string') throw new Error('id is not a string');
  const supplier = await getExtendedSupplier({slug: params.id}, true);
  if (!supplier) return {notFound: true};
  return buildStaticPropsResponse({supplier}, TWENTY_FOUR_HOURS_IN_SECONDS);
};

export default SupplierReviewsPage;

SupplierReviewsPage.getLayout = getToolLayoutWithSupplier;

SupplierReviewsPage.title = 'Supplier Reviews';
